import { isNil } from 'lodash';
import { createElement, ReactNode } from 'react';
import { getCompsPriceRange } from '../../Comps/utils';
import { useBuyerOfferAnalysisContext } from '../../Context/BuyerOfferAnalysisContext';
import { PurchasePriceTooltip } from '../PurchasePriceTooltip';
import {
  getPurchasePriceTermLabel,
  roundAdjustedListPrice,
} from '../common.utils';
import { CommonKeyTermProps } from './useCommonKeyTermLogic';

export const usePurchasePriceLogic = (props: CommonKeyTermProps) => {
  const { recommendation, range } = props;

  const { comparableSet, offerRecs, listing } = useBuyerOfferAnalysisContext();

  const { metrics } = offerRecs?.competitiveness ?? {};

  const bidAsk = metrics?.bidAsk
    ? {
        avg: metrics.bidAsk,
        min: metrics.minMarketBidAskRatio ?? null,
        max: metrics.maxMarketBidAskRatio ?? null,
      }
    : undefined;

  const listPrice = listing?.mlsListing?.listPrice;

  const placeholder = roundAdjustedListPrice(recommendation);

  const getRangeTrackTooltipContent = ({
    children,
    isOpen,
  }: {
    children: ReactNode;
    isOpen: boolean;
  }): ReactNode => {
    return createElement(PurchasePriceTooltip, {
      bidAsk,
      isOpen,
      listPrice,
      placeholder,
      children,
    });
  };

  const getAnchor = () =>
    listPrice
      ? {
          value: listPrice,
          label: 'List Price',
        }
      : undefined;

  const getMainValueLabel = (): ReactNode => {
    return getPurchasePriceTermLabel({
      placeholder,
      listPrice,
      bidAsk,
    });
  };

  const getHighlightedRange = (): [number, number] | undefined => {
    if (!bidAsk) return undefined;

    return [
      (listPrice ?? 0) * (bidAsk?.min ?? 1),
      (listPrice ?? 0) * (bidAsk?.max ?? 1),
    ];
  };

  const getDotColorValue = (value?: number | null): string => {
    if (isNil(value) || isNil(range) || range.length < 2) return 'gray.500';

    if (isNil(listPrice)) return 'gray.500';
    if (!isNil(placeholder) && value >= range[1]) {
      return 'green.500';
    } else if (!isNil(placeholder) && value >= range[0]) {
      return 'yellow.500';
    } else {
      return 'red.500';
    }
  };

  const getRangeLabel = () => {
    const compsPriceRange = getCompsPriceRange(comparableSet);

    if (isNil(compsPriceRange)) return undefined;

    return `Comps: ${compsPriceRange}`;
  };

  return {
    getPlaceholderValue: () => placeholder,
    getAnchor,
    getMainValueLabel,
    getDotColorValue,
    getHighlightedRange,
    getRangeTrackTooltipContent,
    getRangeLabel,
  };
};
